import { useAuthContext } from "@/context/auth-context";
import { GetGroupDto, PatchGroupBodyDto } from "@/lib/gen-api";
import {
  groupControllerCreateGroup,
  groupControllerDeleteGroup,
  groupControllerGetGroup,
  groupControllerGetGroups,
  groupControllerUpdateGroup,
} from "@/lib/gen-api/sdk.gen";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useIsApiReady } from "../hooks/use-api-ready";
import { queryKeys } from "../lib/query-keys";

const UNAUTHENTICATED_GROUPS_DATA = {
  groups: [] as GetGroupDto[],
  isLoading: false,
  error: null,
};

export const useUserGroupsData = () => {
  const { token, authDetails } = useAuthContext();
  const isApiReady = useIsApiReady();

  const {
    data: groups,
    isPending,
    error,
  } = useQuery({
    enabled: isApiReady && !!authDetails?.uuid,
    queryKey: queryKeys.userGroups(authDetails?.uuid),
    queryFn: () =>
      groupControllerGetGroups({
        query: {
          filter: {
            user_uuid: authDetails!.uuid,
          },
        },
      }).then((res) => res.data?.data ?? []),
    staleTime: 60 * 1000,
  });

  if (!token) {
    return UNAUTHENTICATED_GROUPS_DATA;
  }

  return {
    groups: groups ?? [],
    isLoading: isPending,
    error,
  };
};

export const useCreateTeamGroup = () => {
  const queryClient = useQueryClient();
  const { authDetails } = useAuthContext();

  const { mutateAsync: createTeamGroup, isPending: isCreating } = useMutation({
    mutationFn: async (params: {
      name: string;
      slug: string;
      organizationUuid: string;
    }) => {
      const response = await groupControllerCreateGroup({
        body: {
          name: params.name,
          slug: params.slug,
          organizationUuid: params.organizationUuid,
        },
      });
      if (response.response.status !== 201 || !response.data) {
        throw new Error("Failed to create group");
      }

      const created = await groupControllerGetGroup({
        path: { groupUuid: response.data.uuid },
      });
      if (!created.data) {
        throw new Error("Failed to load group");
      }
      const group = created.data;
      queryClient.setQueryData<GetGroupDto[]>(
        queryKeys.userGroups(authDetails?.uuid),
        (current) => {
          const groups = current ?? [];
          if (groups.some((item) => item.uuid === group.uuid)) {
            return groups;
          }
          return [...groups, group];
        },
      );
      return group;
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.userGroups(authDetails?.uuid),
      });
    },
  });

  return { createTeamGroup, isCreating };
};

export const useGroupData = (groupUuid?: string | null) => {
  const {
    data: group,
    isPending,
    error,
  } = useQuery({
    enabled: useIsApiReady() && !!groupUuid,
    queryKey: queryKeys.group(groupUuid!),
    queryFn: () =>
      groupControllerGetGroup({
        path: { groupUuid: groupUuid! },
      }).then((res) => res.data ?? null),
  });

  const queryClient = useQueryClient();

  // Update

  const { mutateAsync: updateGroup } = useMutation({
    mutationFn: async (body: PatchGroupBodyDto) => {
      const response = await groupControllerUpdateGroup({
        path: { groupUuid: groupUuid! },
        body,
      });
      if (!response.response.ok) {
        throw new Error("Failed to update group");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.group(groupUuid!),
      });
      // The global admin list shows fields like the client ID.
      queryClient.invalidateQueries({
        queryKey: queryKeys.groups(),
      });
    },
  });

  // Delete
  const { mutateAsync: deleteGroup } = useMutation({
    mutationFn: async () => {
      const response = await groupControllerDeleteGroup({
        path: {
          groupUuid: groupUuid!,
        },
      });
      if (!response.response.ok) {
        throw new Error("Failed to delete group");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.group(groupUuid!),
      });
    },
  });

  return {
    group: group ?? null,
    updateGroup,
    deleteGroup,
    isLoading: isPending,
    error,
  };
};
